/**
 * Воксельные иконки интерфейса (A5, #21): пиксельная графика 14×14 в стиле макетов (`docs/ux/voxel/`) — плоские
 * залитые формы с тёмной обводкой и светлой/тёмной гранью. Рисуются кодом и выводятся как SVG из прямоугольников:
 * ни одного PNG, поэтому в бюджет первой загрузки не входят (≈ 6 КБ кода). Обводка ставится автоматически вокруг
 * любой закрашенной формы.
 *
 * Имена: `coin`, навыки `skill_<kind>`, вкладки `tab_<id>`, усиления — id из данных баланса (`boost_damage_up`, …).
 */

const N = 14;

/** Палитра: буква → цвет (sRGB). */
const COLORS: Record<string, string> = {
  k: "#1b1f2a",
  w: "#f6f8fc",
  g: "#c9ced8",
  G: "#7d8696",
  P: "#4a505e",
  Y: "#ffd93b",
  y: "#e0a21a",
  o: "#f28a1e",
  r: "#e0402c",
  R: "#99261a",
  b: "#4d97ee",
  B: "#27509f",
  c: "#8fe6f8",
  C: "#3fb0d8",
  n: "#62c850",
  N: "#2f7d2c",
  p: "#a266e8",
  s: "#ecbd8f",
  t: "#8a5a2b",
  T: "#583a1c",
  h: "#ff7aa0",
};

class Grid {
  px: string[][] = Array.from({ length: N }, () => Array<string>(N).fill("."));
  set(x: number, y: number, c: string): void {
    if (x >= 0 && y >= 0 && x < N && y < N) this.px[y]![x] = c;
  }
  rect(x0: number, y0: number, x1: number, y1: number, c: string): void {
    for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) this.set(x, y, c);
  }
  /** Строки фигуры: [y, x0, x1]. */
  rows(c: string, ...r: [number, number, number][]): void {
    for (const [y, a, b] of r) this.rect(a, y, b, y, c);
  }
  disc(cx: number, cy: number, r: number, c: string): void {
    for (let y = 0; y < N; y++)
      for (let x = 0; x < N; x++) if (Math.hypot(x + 0.5 - cx, y + 0.5 - cy) <= r) this.set(x, y, c);
  }
  line(x0: number, y0: number, x1: number, y1: number, c: string, thick = 1): void {
    const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0));
    for (let i = 0; i <= n; i++) {
      const x = Math.round(x0 + ((x1 - x0) * i) / (n || 1));
      const y = Math.round(y0 + ((y1 - y0) * i) / (n || 1));
      for (let a = 0; a < thick; a++) for (let b = 0; b < thick; b++) this.set(x + a, y + b, c);
    }
  }
  /** Тёмная обводка вокруг закрашенного. */
  outline(): this {
    const src = this.px.map((r) => r.slice());
    for (let y = 0; y < N; y++)
      for (let x = 0; x < N; x++) {
        if (src[y]![x] !== ".") continue;
        const near = [
          [x - 1, y],
          [x + 1, y],
          [x, y - 1],
          [x, y + 1],
        ].some(([a, b]) => src[b!]?.[a!] !== undefined && src[b!]![a!] !== "." && src[b!]![a!] !== "k");
        if (near) this.px[y]![x] = "k";
      }
    return this;
  }
}

function flame(g: Grid, x: number, y: number, h: number): void {
  // язык пламени высотой h: красный контур, оранжевая середина, жёлтое ядро
  for (let i = 0; i < h; i++) {
    const half = i < h * 0.3 ? Math.max(0, Math.round(i * 0.6)) : Math.min(3, 1 + Math.round(i * 0.35));
    g.rect(x - half, y + i, x + half, y + i, i < h * 0.25 ? "o" : "r");
  }
  for (let i = Math.floor(h * 0.35); i < h; i++) {
    const half = Math.min(2, Math.round(i * 0.22));
    g.rect(x - half, y + i, x + half, y + i, i < h * 0.7 ? "o" : "Y");
  }
  g.rect(x, y + h - 3, x, y + h - 2, "w");
}

function bullet(g: Grid, x: number, y: number): void {
  g.set(x + 1, y, "o");
  g.rect(x, y + 1, x + 2, y + 4, "Y");
  g.rect(x + 2, y + 1, x + 2, y + 5, "y");
  g.rect(x, y + 5, x + 1, y + 5, "y");
  g.rect(x, y + 6, x + 2, y + 6, "G");
}

function bolt(g: Grid, main: string, shade: string, hi: string): void {
  g.rows(
    main,
    [1, 6, 10],
    [2, 5, 9],
    [3, 4, 8],
    [4, 3, 10],
    [5, 6, 10],
    [6, 6, 9],
    [7, 5, 8],
    [8, 4, 7],
    [9, 4, 6],
    [10, 3, 5],
    [11, 3, 4],
  );
  g.rows(shade, [4, 9, 10], [5, 9, 10], [6, 9, 9], [7, 8, 8], [8, 7, 7], [9, 6, 6], [10, 5, 5], [11, 4, 4]);
  g.rows(hi, [2, 6, 6], [3, 5, 5], [4, 4, 4]);
}

function snowflake(g: Grid, c: string, d: string): void {
  g.rect(1, 6, 12, 7, c);
  g.rect(6, 1, 7, 12, c);
  g.line(2, 2, 11, 11, c);
  g.line(11, 2, 2, 11, c);
  g.rect(5, 5, 8, 8, "w");
  g.set(6, 6, d);
  for (const [x, y] of [
    [1, 1],
    [12, 1],
    [1, 12],
    [12, 12],
    [6, 1],
    [1, 6],
    [12, 6],
    [6, 12],
  ] as const)
    g.set(x, y, "w");
}

function cannon(g: Grid): void {
  g.rect(2, 4, 10, 7, "P");
  g.rect(2, 4, 10, 4, "G");
  g.rect(10, 3, 12, 8, "P");
  g.rect(10, 3, 12, 3, "G");
  g.set(12, 5, "k");
  g.rect(2, 8, 3, 8, "T");
  g.disc(5, 10, 2.6, "t");
  g.disc(5, 10, 1, "T");
}

function swordIcon(g: Grid): void {
  g.line(11, 1, 4, 8, "g", 2);
  g.line(12, 1, 5, 8, "G", 1);
  g.rect(2, 7, 7, 8, "y");
  g.line(3, 6, 7, 10, "y");
  g.rect(1, 10, 3, 12, "t");
  g.set(11, 1, "w");
}

function armorIcon(g: Grid): void {
  g.rows(
    "b",
    [1, 2, 11],
    [2, 2, 11],
    [3, 2, 11],
    [4, 2, 11],
    [5, 2, 11],
    [6, 2, 11],
    [7, 3, 10],
    [8, 3, 10],
    [9, 4, 9],
    [10, 5, 8],
    [11, 6, 7],
  );
  g.rect(7, 2, 11, 7, "B");
  g.rows("B", [8, 7, 10], [9, 7, 9], [10, 7, 8], [11, 7, 7]);
  g.rect(6, 3, 7, 9, "w");
  g.rect(4, 5, 9, 6, "w");
}

function coinsIcon(g: Grid): void {
  g.disc(5, 8.5, 3.6, "y");
  g.disc(4.7, 8.2, 2.7, "Y");
  g.disc(9, 5.5, 3.6, "y");
  g.disc(8.7, 5.2, 2.7, "Y");
  g.rect(8, 3, 9, 7, "o");
  g.set(7, 4, "w");
  g.set(3, 7, "w");
}

/** Знак ∞ в правом нижнем углу: бесконечное усиление отличается от обычного с той же картинкой. */
function endlessMark(g: Grid): void {
  g.rect(6, 9, 13, 13, "k");
  g.rows("c", [10, 8, 8], [10, 11, 11], [11, 7, 7], [11, 9, 10], [11, 12, 12], [12, 8, 8], [12, 11, 11]);
}

const DRAW = {
  // монета со звездой (макет chibi-style-ref): звезда тиснением тёмным золотом
  coin(g) {
    g.disc(7, 7, 5.6, "y");
    g.disc(6.6, 6.6, 4.6, "Y");
    g.rows(
      "y",
      [3, 6, 7],
      [4, 6, 7],
      [5, 3, 10],
      [6, 4, 9],
      [7, 5, 8],
      [8, 4, 9],
      [9, 4, 5],
      [9, 8, 9],
      [10, 3, 4],
      [10, 9, 10],
    );
    g.set(3, 4, "w");
    g.set(4, 3, "w");
  },
  // навыки
  skill_barrage(g) {
    bullet(g, 1, 4);
    bullet(g, 5, 1);
    bullet(g, 9, 4);
    g.rect(1, 12, 3, 12, "G");
    g.rect(5, 9, 7, 9, "G");
  },
  skill_airstrike(g) {
    g.disc(7, 8.5, 3.8, "P");
    g.disc(6, 7.5, 1.4, "G");
    g.rect(5, 3, 9, 4, "r");
    g.rect(3, 1, 4, 3, "r");
    g.rect(10, 1, 11, 3, "r");
    g.rect(6, 5, 8, 5, "R");
    g.line(1, 5, 1, 9, "c");
    g.line(12, 4, 12, 8, "c");
  },
  skill_chainLightning(g) {
    bolt(g, "Y", "o", "w");
    g.set(1, 3, "c");
    g.set(12, 9, "c");
    g.set(11, 2, "c");
  },
  skill_firewall(g) {
    flame(g, 4, 3, 8);
    flame(g, 9, 1, 10);
    g.rect(1, 11, 12, 12, "R");
    g.rect(1, 11, 12, 11, "t");
  },
  skill_freeze(g) {
    snowflake(g, "C", "C");
  },
  // вкладки
  tab_heroes(g) {
    g.rect(3, 6, 10, 12, "s");
    g.rect(3, 2, 10, 5, "b");
    g.rect(3, 5, 10, 5, "B");
    g.rect(2, 5, 11, 5, "B");
    g.rect(4, 8, 5, 9, "k");
    g.rect(8, 8, 9, 9, "k");
    g.rect(5, 11, 8, 11, "R");
    g.rect(5, 2, 6, 3, "c");
  },
  tab_level(g) {
    g.rows("n", [1, 6, 7], [2, 5, 8], [3, 4, 9], [4, 3, 10], [5, 2, 11]);
    g.rect(5, 6, 8, 12, "n");
    g.rect(8, 6, 8, 12, "N");
    g.rect(10, 5, 11, 5, "N");
    g.rect(9, 4, 10, 4, "N");
    g.rect(6, 2, 6, 3, "w");
  },
  tab_stars(g) {
    g.rows(
      "Y",
      [1, 6, 7],
      [2, 6, 7],
      [3, 5, 8],
      [4, 1, 12],
      [5, 2, 11],
      [6, 3, 10],
      [7, 4, 9],
      [8, 4, 9],
      [9, 3, 5],
      [9, 8, 10],
      [10, 2, 4],
      [10, 9, 11],
    );
    g.rows("y", [5, 9, 11], [6, 8, 10], [7, 8, 9], [8, 8, 9], [9, 9, 10], [10, 10, 11], [4, 11, 12]);
    g.rect(6, 3, 6, 4, "w");
    g.set(4, 4, "w");
  },
  tab_tech(g) {
    for (const [x, y] of [
      [6, 1],
      [6, 11],
      [1, 6],
      [11, 6],
    ] as const)
      g.rect(x, y, x + 1, y + 1, "G");
    for (const [x, y] of [
      [2, 2],
      [10, 2],
      [2, 10],
      [10, 10],
    ] as const)
      g.rect(x, y, x + 1, y + 1, "G");
    g.disc(7, 7, 4.6, "g");
    g.disc(7, 7, 1.7, "k");
    g.rect(8, 7, 11, 11, "G");
    g.disc(7, 7, 1.7, "k");
    g.rect(4, 3, 6, 3, "w");
  },
  // усиления
  boost_damage_up(g) {
    swordIcon(g);
  },
  boost_fire_rate(g) {
    for (const x of [1, 6]) {
      g.line(x, 2, x + 4, 7, "o", 2);
      g.line(x + 4, 7, x, 12, "o", 2);
      g.line(x + 1, 3, x + 4, 6, "Y");
    }
  },
  boost_long_range(g) {
    g.rect(3, 6, 10, 7, "b");
    g.rows("b", [3, 2, 3], [4, 2, 4], [5, 2, 5], [8, 2, 5], [9, 2, 4], [10, 2, 3]);
    g.rows("b", [3, 10, 11], [4, 9, 11], [5, 8, 11], [8, 8, 11], [9, 9, 11], [10, 10, 11]);
    g.rect(3, 6, 10, 6, "c");
    g.set(7, 3, "w");
    g.set(7, 10, "w");
  },
  boost_crit_chance(g) {
    g.disc(7, 7, 5.6, "r");
    g.disc(7, 7, 3.9, "w");
    g.disc(7, 7, 2.2, "r");
    g.rect(6, 6, 7, 7, "w");
    g.rect(6, 0, 7, 1, "r");
    g.rect(6, 12, 7, 13, "r");
    g.rect(0, 6, 1, 7, "r");
    g.rect(12, 6, 13, 7, "r");
  },
  boost_cannon_power(g) {
    cannon(g);
    g.rows("Y", [1, 12, 12], [2, 11, 13], [3, 13, 13]);
    g.set(12, 11, "o");
    g.rect(9, 10, 12, 11, "r");
    g.rows("o", [9, 11, 12]);
  },
  boost_hq_armor(g) {
    armorIcon(g);
  },
  boost_coin_rush(g) {
    coinsIcon(g);
  },
  boost_quick_skills(g) {
    g.disc(7, 8, 5, "w");
    g.disc(7, 8, 3.8, "g");
    g.rect(6, 1, 7, 2, "G");
    g.rect(5, 1, 8, 1, "G");
    g.rect(6, 4, 7, 8, "r");
    g.rect(7, 8, 10, 9, "r");
    g.rect(2, 4, 3, 5, "G");
    g.rect(10, 4, 11, 5, "G");
  },
  boost_ignite(g) {
    flame(g, 7, 1, 11);
  },
  boost_crit_damage(g) {
    const pts: [number, number, number, number][] = [
      [7, 1, 7, 12],
      [1, 7, 12, 7],
      [2, 2, 11, 11],
      [11, 2, 2, 11],
    ];
    for (const [a, b, c, d] of pts) g.line(a, b, c, d, "o", 2);
    g.disc(7, 7, 3.6, "r");
    g.disc(7, 7, 2.2, "Y");
    g.rect(6, 6, 7, 7, "w");
  },
  boost_ricochet(g) {
    g.line(1, 11, 5, 4, "c", 2);
    g.line(5, 4, 9, 10, "c", 2);
    g.line(9, 10, 11, 5, "c", 2);
    g.rows("w", [1, 10, 12], [2, 11, 12], [3, 12, 12]);
    g.rect(9, 1, 12, 4, "C");
    g.rect(10, 1, 12, 1, "w");
    g.rect(12, 2, 12, 4, "w");
  },
  boost_multishot(g) {
    for (const [x1, y1] of [
      [2, 1],
      [7, 0],
      [12, 1],
    ] as const)
      g.line(7, 12, x1, y1 + 1, "G", 1);
    for (const [x1, y1] of [
      [2, 1],
      [7, 0],
      [12, 1],
    ] as const) {
      g.rect(x1 - 1, y1 + 1, x1 + 1, y1 + 3, "Y");
      g.set(x1, y1, "o");
    }
    g.rect(5, 11, 9, 12, "t");
  },
  boost_cannon_rapid(g) {
    cannon(g);
    g.rect(12, 9, 13, 10, "P");
    g.rect(10, 11, 11, 12, "P");
    g.rect(12, 12, 13, 12, "g");
    g.rows("Y", [1, 11, 13], [2, 12, 13]);
  },
  boost_skill_power(g) {
    g.disc(7, 7, 5.5, "p");
    g.disc(7.4, 7.4, 3.6, "B");
    g.disc(6.2, 6.2, 2.4, "p");
    g.rows("w", [2, 4, 5], [3, 3, 4]);
    g.rect(8, 8, 9, 9, "Y");
    g.set(10, 3, "w");
    g.set(11, 4, "Y");
  },
  boost_hq_regen(g) {
    g.disc(4.5, 5, 3.4, "h");
    g.disc(9.5, 5, 3.4, "h");
    g.rows("h", [6, 1, 12], [7, 2, 11], [8, 3, 10], [9, 4, 9], [10, 5, 8], [11, 6, 7]);
    g.rows("r", [8, 8, 10], [9, 8, 9], [10, 8, 8]);
    g.rect(6, 3, 7, 9, "w");
    g.rect(4, 5, 9, 6, "w");
    g.set(3, 3, "w");
  },
  boost_frost_aura(g) {
    g.disc(7, 7, 6, "C");
    g.disc(7, 7, 4.8, "c");
    g.rect(6, 3, 7, 11, "w");
    g.rect(3, 6, 11, 7, "w");
    g.line(4, 4, 10, 10, "w");
    g.line(10, 4, 4, 10, "w");
    g.rect(6, 6, 7, 7, "C");
  },
  boost_overdrive(g) {
    bolt(g, "o", "r", "Y");
    g.set(1, 2, "Y");
    g.set(12, 10, "Y");
    g.set(11, 1, "Y");
  },
  boost_endless_damage(g) {
    swordIcon(g);
    endlessMark(g);
  },
  boost_endless_armor(g) {
    armorIcon(g);
    endlessMark(g);
  },
  boost_endless_coins(g) {
    coinsIcon(g);
    endlessMark(g);
  },
} satisfies Record<string, (g: Grid) => void>;

export type PixelIconName = keyof typeof DRAW;

export const PIXEL_ICON_NAMES = Object.keys(DRAW) as readonly PixelIconName[];
export type PixelRun = { color: string; d: string };

const cache = new Map<string, PixelRun[]>();

/** Иконка как набор путей по цветам (прямоугольники по строкам), сетка {@link PIXEL_ICON_SIZE}×{@link PIXEL_ICON_SIZE}. */
export function pixelIcon(name: string): PixelRun[] | null {
  const hit = cache.get(name);
  if (hit) return hit;
  const draw = (DRAW as Record<string, ((g: Grid) => void) | undefined>)[name];
  if (!draw) return null;
  const g = new Grid();
  draw(g);
  g.outline();
  const by = new Map<string, string>();
  for (let y = 0; y < N; y++) {
    let x = 0;
    while (x < N) {
      const c = g.px[y]![x]!;
      let e = x;
      while (e + 1 < N && g.px[y]![e + 1] === c) e++;
      if (c !== ".") by.set(c, `${by.get(c) ?? ""}M${x} ${y}h${e - x + 1}v1h${-(e - x + 1)}z`);
      x = e + 1;
    }
  }
  const runs = [...by].map(([c, d]) => ({ color: COLORS[c] ?? "#f0f", d }));
  cache.set(name, runs);
  return runs;
}

export const PIXEL_ICON_SIZE = N;
